import ChatMessageCall from './ChatMessageCall';
import React from 'react';
import IncomingTranslation from './IncomingTranslation';
import { templateBubbleTitle } from '@src/helpers/TemplateBubble';
import DoneIcon from '@mui/icons-material/Done';
import ErrorIcon from '@mui/icons-material/Error';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import Moment from 'react-moment';
import '../../../../styles/InputRange.css';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MessageDateIndicator from '../MessageDateIndicator';
import ContextChatMessage from './ContextChatMessage';
import ReplyIcon from '@mui/icons-material/Reply';
import ChatMessageVideo from './ChatMessageVideo';
import ChatMessageImage from './ChatMessageImage';
import ChatMessageDocument from './ChatMessageDocument';
import ChatMessageVoice from './ChatMessageVoice';
import ChatMessageTemplate from './ChatMessageTemplate';
import ChatAssignmentEventView from '../../../ChatAssignmentEventView';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import * as EventStyle from '../../../ChatAssignmentEventView/ChatAssignmentEventView.styles';
import ChatTaggingEvent from '../../../ChatTaggingEvent';
import ChatMessageLocation from './ChatMessageLocation';
import { useTranslation } from 'react-i18next';
import InteractiveMessage from './InteractiveMessage';
import OrderMessage from './OrderMessage';
import ContactsMessage from './ContactsMessage';
import PrintMessage from '../../../PrintMessage';
import ChatMessageReferral from './ChatMessageReferral';
import { setPreviewMediaObject } from '@src/store/reducers/previewMediaObjectReducer';
import PreviewMediaModel from '../../../../api/models/PreviewMediaModel';
import { ATTACHMENT_TYPE_IMAGE, ATTACHMENT_TYPE_VIDEO } from '@src/Constants';
import { useAppDispatch } from '@src/store/hooks';
import ChatMessageErrors from '@src/components/ChatMessageErrors';
import { clone } from '@src/helpers/ObjectHelper';
import { InsertEmoticon } from '@mui/icons-material';
import useReactions from '@src/hooks/useReactions';
import { setState } from '@src/store/reducers/UIReducer';
import { Template } from '@src/types/templates';
import {
	generateImageLink,
	generateStickerLink,
	generateVideoLink,
	getEchoOriginLabel,
	getMessageCaption,
	getMessageTimestamp,
	getSenderName,
	hasAnyStatus,
	hasMediaToPreview,
	isDeliveredOrRead,
	isJustSent,
	isPending,
	isRead,
	isUnsupportedMessageType,
} from '@src/helpers/MessageHelper';
import { Message, MessageType } from '@src/types/messages';
import { generateInitialsHelper } from '@src/helpers/Helpers';
import MissingContent from './MissingContent';
import * as Styled from './ChatMessage.styles';

interface Props {
	localSendState?: string;
	data: Message;
	reactionsHistory?: Message[];
	templateData?: Template;
	displaySender?: boolean;
	displayDate?: boolean;
	isExpired?: boolean;
	contactProvidersData?: { [key: string]: any };
	onOptionsClick?: (e: React.MouseEvent, data: Message) => void;
	onQuickReactionsClick?: (e: React.MouseEvent, data: Message) => void;
	onReactionDetailsClick?: (e: React.MouseEvent, data: Message) => void;
	goToMessageId?: (msgId: string, timestamp: number) => void;
	retryMessage?: (message: Message) => void;
	disableMediaPreview?: boolean;
	setMessageWithStatuses?: (message?: Message) => void;
	isActionsEnabled?: boolean;
	isInfoClickable?: boolean;
}

const iconStyles = {
	fontSize: '15px',
};

const ChatMessage: React.FC<Props> = ({
	localSendState,
	data,
	reactionsHistory,
	templateData,
	displaySender,
	displayDate,
	isExpired,
	contactProvidersData,
	onOptionsClick,
	onQuickReactionsClick,
	onReactionDetailsClick,
	goToMessageId,
	retryMessage,
	disableMediaPreview,
	setMessageWithStatuses,
	isActionsEnabled = false,
	isInfoClickable = true,
}) => {
	const { t } = useTranslation();
	const compactTitle = window.config?.APP_ENV_NAME === 'cvparc-shadow' ? templateBubbleTitle(data, templateData) : null;
	const templateContent = data.waba_payload?.type === MessageType.template ? <ChatMessageTemplate data={data} templateData={templateData} onPreview={(type, source) => onPreview(type, source)} onOptionsClick={(e) => onOptionsClick?.(e, data)} /> : <PrintMessage className="wordBreakWord" message={data.waba_payload?.text?.body ?? ''} linkify={true} />;

	const dispatch = useAppDispatch();

	const { reactions, reactionsWithCount } = useReactions({
		reactionsHistory,
	});

	const echoOriginLabel = getEchoOriginLabel(data);

	const onPreview = (type: string, source: string) => {
		if (!disableMediaPreview) {
			const previewData = new PreviewMediaModel(
				getSenderName(data),
				generateInitialsHelper(getSenderName(data)),
				type,
				source,
				getMessageTimestamp(data) ?? -1
			);

			dispatch(setPreviewMediaObject(previewData));
		}
	};

	const dateFormat = 'H:mm';

	return (
		<Styled.ChatMessageOuter
			id={'message_' + data.id}
			$isOutgoing={data.from_us}
			$type={data.waba_payload?.type}
		>
			{displayDate && (
				<MessageDateIndicator timestamp={getMessageTimestamp(data)} />
			)}

			{data.assignment_event && (
				<ChatAssignmentEventView data={data.assignment_event} />
			)}

			{data.tagging_event && <ChatTaggingEvent data={data.tagging_event} />}

			{data.internal_event && <EventStyle.Container><EventStyle.Content><EventStyle.Wrapper><EventStyle.Title><InfoOutlinedIcon aria-hidden="true" style={{fontSize:14,verticalAlign:'-2px',marginRight:5}} />{data.internal_event.text}{data.internal_event.reason && <> · {data.internal_event.reason}</>} · <span style={{color:'rgba(0, 0, 45, 0.6)'}}>{new Date(Number(data.waba_payload?.timestamp) * 1000).toLocaleTimeString('es-ES', {hour:'2-digit',minute:'2-digit'})}</span></EventStyle.Title></EventStyle.Wrapper></EventStyle.Content></EventStyle.Container>}
			{data.waba_payload?.type === MessageType.call && <div>					{(displaySender || displayDate) && (
						<Styled.SenderName $isOutgoing={data.from_us}>
							{data.from_us
								? echoOriginLabel
									? t(echoOriginLabel)
									: getSenderName(data)
								: contactProvidersData?.[data.waba_payload?.wa_id ?? data.customer_wa_id ?? '']?.[0]
										?.name ?? getSenderName(data)}
						</Styled.SenderName>
					)}<ChatMessageCall data={data} isFirstInGroup={Boolean(displaySender || displayDate)} /></div>}
			{data.waba_payload?.type !== MessageType.call && !data.assignment_event && !data.tagging_event && !data.internal_event && (
				<div>
					{(displaySender || displayDate) && (
						<Styled.SenderName $isOutgoing={data.from_us}>
							{data.from_us
								? echoOriginLabel
									? t(echoOriginLabel)
									: getSenderName(data)
								: contactProvidersData?.[data.waba_payload?.wa_id ?? data.customer_wa_id ?? '']?.[0]
										?.name ?? getSenderName(data)}
						</Styled.SenderName>
					)}


					<Styled.ChatMessage
						$isAI={window.config?.APP_ENV_NAME === 'cvparc-shadow' && data.from_us && data.sender?.id === 9}
						$type={data.waba_payload?.type ?? MessageType.none}
						$isOutgoing={data.from_us}
						$isReceived={data.from_us && isRead(data)}
						$hasMedia={hasMediaToPreview(data)}
						$hasReaction={reactions.length > 0}
						$isSenderHidden={!displaySender && !displayDate}
						$isFirstInGroup={displaySender || displayDate}
						$isFailed={data.is_failed}
					>
						{isActionsEnabled && (
							<Styled.Actions
								className="chat-message-actions"
								$isRight={!data.from_us}
								$isNonText={data.waba_payload?.type !== MessageType.text}
								$isExpired={!!isExpired}
							>
								{!isExpired && (
									<Styled.Action
										onClick={(event) => onQuickReactionsClick?.(event, data)}
									>
										<InsertEmoticon />
									</Styled.Action>
								)}

								{((data.from_us &&
									data.waba_payload?.type === MessageType.text) ||
									data.waba_payload?.type === MessageType.audio) && (
									<Styled.Action
										onClick={(event) => onOptionsClick?.(event, data)}
									>
										<ExpandMoreIcon />
									</Styled.Action>
								)}
							</Styled.Actions>
						)}

						{data.context?.forwarded && (
							<Styled.Forwarded>
								<ReplyIcon />
								<span>{t('Forwarded')}</span>
							</Styled.Forwarded>
						)}

						{data.context && (
							<ContextChatMessage
								contextMessage={data.context}
								goToMessageId={goToMessageId}
							/>
						)}

						{data.waba_payload?.referral && (
							<ChatMessageReferral
								data={data}
								onPreview={onPreview}
								onOptionsClick={(e: React.MouseEvent) =>
									onOptionsClick?.(e, data)
								}
							/>
						)}

						{data.waba_payload?.type === MessageType.image && (
							<ChatMessageImage
								data={data}
								source={generateImageLink(data)}
								onPreview={() =>
									onPreview(ATTACHMENT_TYPE_IMAGE, generateImageLink(data))
								}
							/>
						)}

						{data.waba_payload?.type === MessageType.video && (
							<ChatMessageVideo
								source={generateVideoLink(data)}
								onPreview={() =>
									onPreview(ATTACHMENT_TYPE_VIDEO, generateVideoLink(data))
								}
								onOptionsClick={(e) => onOptionsClick?.(e, data)}
							/>
						)}

						{(data.waba_payload?.type === MessageType.voice ||
							data.waba_payload?.type === MessageType.audio) && (
							<ChatMessageVoice data={data} />
						)}

						{data.waba_payload?.type === MessageType.document && (
							<ChatMessageDocument data={data} />
						)}

						{data.waba_payload?.type === MessageType.sticker && (
							<img className="chat__media chat__sticker" src={generateStickerLink(data)} alt={getMessageCaption(data) || 'Sticker'} />
						)}

						{data.waba_payload?.type === MessageType.location && (
							<ChatMessageLocation data={data} />
						)}

						{compactTitle && <details className="clinic-template-bubble"><summary>🐾 {compactTitle}<span className="clinic-template-chevron" aria-hidden="true">⌄</span></summary><div className="clinic-template-expanded">{templateContent}</div></details>}
						{!compactTitle && data.waba_payload?.type === MessageType.template && (
							<ChatMessageTemplate
								data={data}
								templateData={templateData}
								onPreview={onPreview}
								onOptionsClick={(e: React.MouseEvent) =>
									onOptionsClick?.(e, data)
								}
							/>
						)}

						{data.waba_payload?.type === MessageType.interactive && (
							<InteractiveMessage data={data} />
						)}

						{data.waba_payload?.type === MessageType.order && (
							<OrderMessage data={data} />
						)}

						{data.waba_payload?.type === MessageType.contacts && (
							<ContactsMessage data={data} />
						)}

						{isUnsupportedMessageType(data.waba_payload?.type) && (
							<MissingContent>{t('Unsupported message')}</MissingContent>
						)}

						{!compactTitle && (data.waba_payload?.text?.body ??
						getMessageCaption(data) ??
						data.waba_payload?.button?.text ??
						data.waba_payload?.interactive?.button_reply?.title) ? (
							<PrintMessage
								className="wordBreakWord"
								message={
									data.waba_payload?.text?.body ??
									getMessageCaption(data) ??
									data.waba_payload?.button?.text ??
									data.waba_payload?.interactive?.button_reply?.title ??
									''
								}
								linkify={true}
							/>
						) : (
							'\u00A0'
						)}

						{!data.from_us && window.config?.APP_ENV_NAME==='cvparc-shadow' && (data.waba_payload?.text?.body || data.waba_payload?.transcription) && <IncomingTranslation id={String(data.id)} text={data.waba_payload?.text?.body || data.waba_payload?.transcription || ''}/>}
						{!hasAnyStatus(data) && (
							<ChatMessageErrors data={data} retryMessage={retryMessage} />
						)}

						<span
							className="chat__message__info"
							title={localSendState==='uncertain'?'Sin confirmar · Comprueba la conversación antes de repetirlo':localSendState==='failed'?'No enviado':isPending(data) && !!(data.waba_payload as any).from && !!(data.waba_payload as any).to && !(data.waba_payload as any).staff_user_id?'Estado de entrega no disponible':undefined}
							onClick={() => {
								if (data.from_us && isInfoClickable) {
									const clonedMessage = clone(data) as Message;
									// Injecting reactions
									clonedMessage.reactions = reactions;
									setMessageWithStatuses?.(clonedMessage);
									dispatch(setState({ isMessageStatusesVisible: true }));
								}
							}}
						>
							<span className="chat__timestamp">
								{data.waba_payload?.edited_at ? 'Editado · ' : ''}
								<Moment
									date={getMessageTimestamp(data)}
									format={dateFormat}
									unix
								/>
							</span>

							{(!data.is_failed || hasAnyStatus(data)) && data.from_us && (
								<>
									{isPending(data) && (localSendState || (data.waba_payload as any).report_pending) && <DoneIcon className="chat__iconDone" aria-hidden="true" style={{...iconStyles,visibility:'hidden'}} />}
									{isPending(data) && !localSendState && !(data.waba_payload as any).report_pending && !((data.waba_payload as any).from && (data.waba_payload as any).to && !(data.waba_payload as any).staff_user_id) && (
										<AccessTimeIcon
											className="chat__iconPending"
											color="inherit"
											style={iconStyles}
										/>
									)}

									{isJustSent(data) && (
										<DoneIcon
											className="chat__iconDone"
											color="inherit"
											style={iconStyles}
										/>
									)}

									{isDeliveredOrRead(data) && (
										<Styled.DoneAllIcon
											className="chat__iconDoneAll"
											color="inherit"
											style={iconStyles}
										/>
									)}
								</>
							)}

							{data.is_failed && !hasAnyStatus(data) && (
								<ErrorIcon
									className="chat__iconError"
									color="inherit"
									style={iconStyles}
								/>
							)}
						</span>

						{reactionsWithCount && reactionsWithCount.length > 0 && (
							<Styled.Reactions
								onClick={(event) => onReactionDetailsClick?.(event, data)}
							>
								{reactionsWithCount.map((item) => (
									<Styled.Reaction key={item.emoji}>
										<PrintMessage message={item.emoji} />
									</Styled.Reaction>
								))}
								{reactionsWithCount.length > 1 && (
									<Styled.ReactionCount>
										{reactionsWithCount.length}
									</Styled.ReactionCount>
								)}
							</Styled.Reactions>
						)}

						<div style={{ clear: 'both' }} />
					</Styled.ChatMessage>
				</div>
			)}
		</Styled.ChatMessageOuter>
	);
};

export default ChatMessage;
